import { useTranslation } from 'react-i18next'
import { shallowEqual, useSelector } from 'react-redux'

import { TableHeadCellPropsType, useAppDispatch } from '@/state'
import { FormStatusType, DialogsEnum } from '@/enums'
import { authSelector } from '@/state/auth/slices/index.slices'

import { usersSelector } from '@/state/user/slices/index.slices'
import { GlobalUIActions } from '@/state/ui/global/slices/index.slices'
import { UserLibraryColumnProps, UserResponse } from '@/state/user/models'

import useTableSort from '@/hooks/table/use-table-sort.hook'
import { useUsersDialog, useUsersActions } from './'

export const useUsers = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const { deleteUser, setFormMode, setCurrentUserAction } = useUsersActions()

  const { toggleDialog } = useUsersDialog()

  const { users, pagination, isLoading } = useSelector(
    usersSelector,
    shallowEqual,
  )

  const { userSession } = useSelector(authSelector)

  const headCells: TableHeadCellPropsType<UserLibraryColumnProps>[] = [
    { id: 'isActive', label: t('labels.status'), sort: true },
    { id: 'firstName', label: t('name'), width: 200, sort: true },
    { id: 'email', label: t('email'), width: 250, sort: true },
    { id: 'username', label: t('labels.username'), width: 200, sort: true },
    { id: 'role', label: t('labels.role'), sort: true },
  ]

  const sortedInitialState = 'username'

  const { sortedRows, createSortHandler, order, orderBy } = useTableSort<
    UserLibraryColumnProps,
    keyof UserLibraryColumnProps
  >(users, sortedInitialState, 'asc')

  const handleDeleteUser = (
    anchorPosition: { top: number; left: number },
    userId: string,
  ) => {
    dispatch(
      GlobalUIActions.setPopoverConfirmAction({
        action: t('messages.delete'),
        anchorPosition,
        open: true,
        type: t('user').toLowerCase(),
        handler: () => deleteUser({ id: userId }),
      }),
    )
  }

  const handleEditUser = (user: UserResponse) => {
    setFormMode(FormStatusType.EDIT)
    toggleDialog(true, DialogsEnum.DIALOG_USERS)
    setCurrentUserAction(user)
  }

  return {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    pagination,
    isLoading,
    headCells,
    userSession,
    handleEditUser,
    handleDeleteUser,
  }
}
